// Standalone page flow — replaced previous modal-based approach.
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { IconButton } from '@/components/atoms/IconButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/molecules/Card';
import { useActivities } from '@/hooks/useBackofficeData';
import { buildRoute, ROUTES } from '@/constants/routes';
import type { ActivityFormData } from '../ActivityEditorPage/ActivityEditorPage.types';
import { ActivityForm } from '../ActivityEditorPage/components/ActivityForm';
import styles from './ActivityFormPage.module.scss';

export function ActivityFormPage() {
  const navigate = useNavigate();
  const { storyId: storyId, activityId } = useParams<{ storyId: string; activityId: string }>();
  const [sidebarContainer, setSidebarContainer] = React.useState<HTMLDivElement | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true);

  const { activities, isLoading, saveActivity } = useActivities(storyId);

  const isCreateMode = !activityId || activityId === 'new';
  const activity = React.useMemo(() => {
    if (isCreateMode || !activityId) return undefined;
    return activities.find((a) => a.id === activityId);
  }, [activities, activityId, isCreateMode]);

  const handleBack = () => {
    const url = buildRoute(ROUTES.BACKOFFICE.STORY_EDIT, { id: storyId ?? '' });
    navigate(`${url}?tab=activities`);
  };

  const handleSave = async (formData: ActivityFormData) => {
    await saveActivity(formData as Parameters<typeof saveActivity>[0]);
    handleBack();
  };

  const handleCancel = () => {
    handleBack();
  };

  if (!storyId) {
    return (
      <div className={styles.activityFormPage}>
        <p className={styles.activityFormPage__error}>Story ID no encontrado</p>
      </div>
    );
  }

  if (isLoading && !isCreateMode) {
    return (
      <div className={styles.activityFormPage}>
        <div className={styles.activityFormPage__loading}>Cargando actividad...</div>
      </div>
    );
  }

  if (!isCreateMode && !activity) {
    return (
      <div className={styles.activityFormPage}>
        <p className={styles.activityFormPage__error}>Actividad no encontrada</p>
        <IconButton icon={ArrowLeft} onClick={handleBack} aria-label="Volver al listado" />
      </div>
    );
  }

  return (
    <div className={styles.activityFormPage}>
      <div className={styles.activityFormPage__toolbar}>
        <div className={styles.activityFormPage__header}>
          <IconButton icon={ArrowLeft} onClick={handleBack} aria-label="Volver al listado" />
          <h1 className={styles.activityFormPage__title}>
            {isCreateMode ? 'Crear actividad' : 'Editar actividad'}
          </h1>
          <div className={styles.activityFormPage__headerActions}>
            <IconButton
              icon={Save}
              variant="primary"
              onClick={() => (document.getElementById('activity-edit-form') as HTMLFormElement | null)?.requestSubmit()}
              aria-label="Guardar"
            />
            <IconButton
              icon={isSidebarOpen ? PanelRightClose : PanelRightOpen}
              onClick={() => setIsSidebarOpen((v) => !v)}
              aria-label={isSidebarOpen ? 'Ocultar panel de información' : 'Mostrar panel de información'}
            />
          </div>
        </div>
      </div>

      <div className={styles.activityFormPage__body} data-sidebar-open={isSidebarOpen}>
        <div className={styles.activityFormPage__main}>
          <ActivityForm
            mode={isCreateMode ? 'create' : 'edit'}
            activity={activity}
            onSave={handleSave}
            onCancel={handleCancel}
            nextOrder={activities.length + 1}
            layout="editor"
            sidebarPortalTarget={sidebarContainer}
            formId="activity-edit-form"
            storyId={storyId}
          />
        </div>
        {isSidebarOpen && (
        <aside className={styles.activityFormPage__sidebar}>
          <Card className={styles.activityFormPage__sidebarCard}>
            <CardHeader>
              <CardTitle>Información</CardTitle>
            </CardHeader>
            <CardContent className={styles.activityFormPage__sidebarContent}>
              <div ref={(el) => setSidebarContainer(el)} />
            </CardContent>
          </Card>
        </aside>
        )}
      </div>
    </div>
  );
}
